import { useState } from "react";
import "./Projects.css";
import { Button } from "./Button";
import { Card, EmptyState } from "./Card";
import { Field, Input } from "./Field";
import { DashProgress } from "./Dash";
import { STATUS, STATUS_KEYS, StatusPill } from "./Status";
import type { StatusKey } from "./Status";
import type { Project } from "../lib/project";
import { tx } from "../i18n";

/* ══ 일감 › 프로젝트의 부품(t-in-port-work-projects) ══
   pr.b3x.app 의 카드 목록 · 상세를 b3x-in 으로 옮겼다. 예전에는 git 저장소 화면 아래 표(Figma 25:2)였다 —
   프로젝트가 일감의 축(이슈 · 마일스톤이 거기 선다)이라 일감 아래로 뺐다. 판은 공통 .b-card · .b-check · .b-meter 다. */

export type RepoRef = { id: string; label: string };

export const projectHref = (id: string) => `#/projects?p=${encodeURIComponent(id)}`;

/** 카드 한 장 — 이름 · id · 붙은 리포(한 줄, 넘치면 말줄임) · 「리포 n · 열린 이슈 n / 전체 · 마일스톤 n」.
 *  누르면 상세(주소 `?p=`). 수는 서버가 센 값 그대로다. */
export function ProjectCard({ p, repoLabel }: { p: Project; repoLabel: (id: string) => string }) {
  const names = p.repositoryIds.map(repoLabel);
  return (
    <a className="b-card is-interactive pcard" href={projectHref(p.id)}>
      <span className="pcard__head">
        <span className="pcard__name">{p.name}</span>
        <code className="pcard__id">{p.id}</code>
      </span>
      <span className="pcard__repos" title={names.join(", ") || undefined}>
        {names.length ? names.join(" · ") : tx("project.noRepos")}
      </span>
      <span className="pcard__facts">
        {tx("projects.facts", { repos: p.repositoryIds.length, open: p.openTaskCount, all: p.taskCount, ms: p.milestoneCount })}
      </span>
    </a>
  );
}

export function ProjectCards({ projects, repoLabel }: { projects: Project[]; repoLabel: (id: string) => string }) {
  return (
    <div className="pcards">
      {projects.map((p) => (
        <ProjectCard key={p.id} p={p} repoLabel={repoLabel} />
      ))}
    </div>
  );
}

/** 새 프로젝트 — 이름(필수) · id(비우면 서버가 만든다). 실패 문장은 부르는 쪽이 위에 띄운다. */
export function ProjectForm({ busy, onCreate, onCancel }: { busy?: boolean; onCreate: (name: string, id: string) => void; onCancel: () => void }) {
  const [name, setName] = useState("");
  const [id, setId] = useState("");
  return (
    <Card title={tx("projects.new")}>
      <Field label={tx("project.name")}>{(fid) => <Input id={fid} value={name} autoFocus onChange={(e) => setName(e.target.value)} />}</Field>
      <Field label={tx("project.id")} hint={tx("project.idHint")}>
        {(fid) => <Input id={fid} value={id} placeholder="in.b3x.app" onChange={(e) => setId(e.target.value)} />}
      </Field>
      <div className="pform__acts">
        <Button onClick={onCancel}>{tx("common.action.cancel")}</Button>
        <Button variant="primary" loading={busy} disabled={!name.trim()} onClick={() => onCreate(name.trim(), id.trim())}>
          {tx("project.create")}
        </Button>
      </div>
    </Card>
  );
}

/** 프로젝트가 없을 때 — 리포가 있으면 「리포 N개로 만들기」 한 번으로 끝난다(옛 표의 빠른 만들기 그대로). */
export function ProjectsEmpty({ repos, canEdit, busy, onQuick }: { repos: number; canEdit: boolean; busy?: boolean; onQuick: () => void }) {
  if (repos && canEdit)
    return (
      <Card>
        <p className="pempty__text">{tx("project.emptyWithRepos", { n: repos })}</p>
        <Button variant="primary" loading={busy} onClick={onQuick}>
          {tx("project.quick", { n: repos })}
        </Button>
      </Card>
    );
  return <EmptyState title={tx("project.empty")} desc={tx("projects.emptyDesc")} />;
}

/** 리포 붙이기/떼기 — 체크하면 **바로 저장**한다(PUT 통째로). 관리자가 아니면 붙은 리포만 읽기로 보인다. */
export function ProjectRepoBind({
  p,
  repos,
  canEdit,
  busy,
  onToggle,
}: {
  p: Project;
  repos: RepoRef[];
  canEdit: boolean;
  busy?: boolean;
  onToggle: (rid: string) => void;
}) {
  const label = (rid: string) => repos.find((r) => r.id === rid)?.label ?? rid;
  return (
    <Card title={tx("projects.repos", { n: p.repositoryIds.length })}>
      {canEdit ? (
        <>
          <p className="pbind__lead">{tx("project.bindLead", { name: p.name })}</p>
          {repos.length ? (
            <div className="pbind__checks">
              {repos.map((r) => (
                <label key={r.id} className="b-check">
                  <input type="checkbox" checked={p.repositoryIds.includes(r.id)} disabled={busy} onChange={() => onToggle(r.id)} />
                  <span>{r.label}</span>
                </label>
              ))}
            </div>
          ) : (
            <p className="pbind__lead">{tx("projects.bindNone")}</p>
          )}
        </>
      ) : p.repositoryIds.length ? (
        <ul className="pbind__list">
          {p.repositoryIds.map((rid) => (
            <li key={rid}>{label(rid)}</li>
          ))}
        </ul>
      ) : (
        <p className="pbind__lead">{tx("project.noRepos")}</p>
      )}
    </Card>
  );
}

/** 이슈 현황 — 완료 / 전체 진행률과 단계마다 수. 이 화면이 받은 `GET /tasks` 를 그 프로젝트로 접는다.
 *  0 인 단계는 안 그린다(취소 포함 — 진행률 분모에서도 뺀다: 대시보드와 같은 셈). */
export function ProjectIssueStats({ statuses, boardHref }: { statuses: StatusKey[]; boardHref: string }) {
  const n = (s: StatusKey) => statuses.filter((x) => x === s).length;
  const total = statuses.filter((s) => s !== "cancelled").length;
  return (
    <Card title={tx("projects.issues")}>
      <DashProgress done={n("done")} total={total} />
      {statuses.length ? (
        <div className="pstats">
          {STATUS_KEYS.filter((s) => n(s) > 0).map((s) => (
            <span key={s} className="pstats__item" aria-label={`${STATUS[s].label} ${n(s)}`}>
              <StatusPill status={s} /> <span className="tnum">{n(s)}</span>
            </span>
          ))}
        </div>
      ) : null}
      <a className="pstats__link" href={boardHref}>
        {tx("projects.toBoard")} →
      </a>
    </Card>
  );
}
